import axiosInstance from "@/api";

/**
 * Organisation API interfaces
 */
export interface Organisation {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  isActive?: boolean;
  isSoftDeleted?: boolean;
}

/**
 * Organisation event response structure
 */
export interface OrganisationEventResponse {
  eventId: string;
  streamName: string;
  type: string;
  payload: Organisation;
  userId: string;
  username: string;
  sentAt: string | null;
  createdAt: string;
  error: boolean;
}

/**
 * Clinic API interfaces
 */
export interface Clinic {
  id: string;
  name: string;
  organisationId: string;
  address?: string;
  phoneNumber?: string;
  createdAt: string;
  updatedAt: string;
  isActive?: boolean;
  isSoftDeleted?: boolean;
  hospitalCode?: string;
  departmentCode?: string;
}

/**
 * Clinic event response structure
 */
export interface ClinicEventResponse {
  eventId: string;
  streamName: string;
  type: string;
  payload: Clinic;
  userId: string;
  username: string;
  sentAt: string | null;
  createdAt: string;
  error: boolean;
}

/**
 * Patient API interfaces
 */
export interface Patient {
  id: string;
  name: string;
  clinicId: string;
  organisationId: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Visit API interfaces
 */
export interface Visit {
  id: string;
  patientId: string;
  clinicId: string;
  status: string;
  startTime: string;
  endTime?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Patient creation DTO
 */
export interface CreatePatientDto {
  clinicId: string;
  organisationId: string;
  name?: string;
}

/**
 * Visit creation DTO
 */
export interface CreateVisitDto {
  clinicId: string;
  patientId: string;
}

/**
 * Organisation API functions
 */
export const fakeOrganisation = () => {
  const url = `/api/patient/fake-organisation`;
  return axiosInstance.get<Organisation[]>(url, {});
};

/**
 * Get Clinics by organisation ID
 * @param organisationId - Organisation ID
 */
export const getClinic = (organisationId: string | null) => {
  const url = organisationId ? `/api/patient/fake-clinic/${organisationId}` : "";
  return axiosInstance.get<ApiResponse<ClinicEventResponse>>(url, {});
};

/**
 * Patient API functions
 */
export const createPatient = (data: CreatePatientDto) => {
  const url = `/api/patient/create-patient`;
  return axiosInstance.post<Patient, CreatePatientDto>(url, data);
};

/**
 * Visit API functions
 */
export const createVisit = (data: CreateVisitDto) => {
  const url = `/api/outpatient/visits`;
  return axiosInstance.post<Visit, CreateVisitDto>(url, data);
};

/**
 * Direct API calls (non-hook) for one-time operations
 */
import { fetcher, postFetcher } from "@/api/fetcher";
import type { ApiResponse } from "@/api/types";

/**
 * Organisation direct API calls
 */
export const organisationDirectApi = {
  /**
   * Get all organisations
   * @returns Promise with organisations data
   */
  getAll: async () => {
    try {
      return await fetcher<ApiResponse<OrganisationEventResponse>>(
        `/api/patient/fake-organisation`,
      );
    } catch (error) {
      console.error("Error fetching organisations:", error);
      throw error;
    }
  },
};

/**
 * Clinic direct API calls
 */
export const clinicDirectApi = {
  /**
   * Get clinics by organisation ID
   * @param organisationId - Organisation ID
   * @returns Promise with clinics data
   */
  getByOrganisation: async (organisationId: string) => {
    try {
      return await fetcher<Clinic[]>(`/api/patient/fake-clinic/${organisationId}`);
    } catch (error) {
      console.error("Error fetching clinics:", error);
      throw error;
    }
  },
};

/**
 * Patient direct API calls
 */
export const patientDirectApi = {
  /**
   * Create a new patient
   * @param data - Patient creation data
   * @returns Promise with created patient data
   */
  create: async (data: CreatePatientDto) => {
    try {
      return await postFetcher<Patient, CreatePatientDto>("/api/patient/fake-patient", data);
    } catch (error) {
      console.error("Error creating patient:", error);
      throw error;
    }
  },
};

/**
 * Visit direct API calls
 */
export const visitDirectApi = {
  /**
   * Create a new visit
   * @param data - Visit creation data
   * @returns Promise with created visit data
   */
  create: async (data: CreateVisitDto) => {
    try {
      return await postFetcher<Visit, CreateVisitDto>("/api/outpatient/visits", data);
    } catch (error) {
      console.error("Error creating visit:", error);
      throw error;
    }
  },
};

/**
 * Health check direct API calls
 */
export const healthDirectApi = {
  /**
   * Check API health
   * @returns Promise with health check response
   */
  checkOutpatientHealth: async () => {
    try {
      return await axiosInstance.get(`/api/outpatient/health`);
    } catch (error) {
      console.error("Error checking health:", error);
      throw error;
    }
  },
  checkPatientHealth: async () => {
    try {
      return await axiosInstance.get(`/api/patient/health`);
    } catch (error) {
      console.error("Error checking health:", error);
      throw error;
    }
  },
  checkProcedureHealth: async () => {
    try {
      return await axiosInstance.get(`/api/procedure/health`);
    } catch (error) {
      console.error("Error checking health:", error);
      throw error;
    }
  },
  checkQueueHealth: async () => {
    try {
      return await axiosInstance.get(`/api/queue/health`);
    } catch (error) {
      console.error("Error checking health:", error);
      throw error;
    }
  },
};

// Export convenient direct API functions
export const getOrganisations = organisationDirectApi.getAll;
export const getClinicsByOrganisation = clinicDirectApi.getByOrganisation;
export const checkOutpatientHealth = healthDirectApi.checkOutpatientHealth;
export const checkPatientHealth = healthDirectApi.checkPatientHealth;
export const checkProcedureHealth = healthDirectApi.checkProcedureHealth;
export const checkQueueHealth = healthDirectApi.checkQueueHealth;
